import React from 'react';
import { Container, Typography, Box, Paper, Grid, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { CheckCircleOutline, EmojiEvents } from '@mui/icons-material';

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  background: 'rgba(255, 255, 255, 0.04)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(180deg, #4CAF50 0%, #2196F3 100%)',
  },
}));

const RoleBlock = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: 'rgba(255,255,255,0.025)',
  borderRadius: '12px',
  border: '1px solid rgba(255,255,255,0.07)',
}));

const CompanyLogo = styled('img')({
  width: '72px',
  height: '72px',
  objectFit: 'contain',
  marginRight: '20px',
  backgroundColor: '#fff',
  padding: '6px',
  borderRadius: '10px',
  flexShrink: 0,
});

const AwardBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(255, 193, 7, 0.10)',
  border: '1px solid rgba(255, 193, 7, 0.30)',
  borderRadius: '20px',
  padding: '6px 16px',
  color: '#FFC107',
  fontSize: '13px',
  fontWeight: 600,
});

const MetricChip = styled(Chip)({
  backgroundColor: 'rgba(76,175,80,0.10)',
  color: '#4CAF50',
  border: '1px solid rgba(76,175,80,0.22)',
  fontSize: '11.5px',
  height: '24px',
  fontWeight: 600,
});

const TagChip = styled(Chip)({
  fontSize: '11px',
  height: '22px',
});

// ─── Data ─────────────────────────────────────────────────────────────────────

const roles = [
  {
    title: 'Technical PM — Consumer Product & Growth',
    period: 'Jan 2024 – Present',
    metrics: ['+40% Activation', '50K+ MAU', '25%+ Engagement Lift', 'KYC Drop-off 34%→14%'],
    achievements: [
      {
        bold: 'PLG Activation & Onboarding:',
        text: ' Drove +40% new-user activation by redesigning the onboarding funnel using Mixpanel cohort analysis; implemented progressive disclosure flow, cutting KYC drop-off from 34% → 14% and growing MAU to 50K+.',
      },
      {
        bold: 'Consumer App Revamp (500K users):',
        text: ' Owned full redesign — rebuilt Information Architecture and core flows in React/TypeScript; drove 25%+ lift in session engagement, tracked via Mixpanel + Google Analytics.',
      },
      {
        bold: 'Retention & Growth Loops:',
        text: ' Established weekly KPI reviews and A/B experimentation cadence; continuously optimised activation and retention funnels through data-backed iterative releases aligned to North Star Metrics.',
      },
      {
        bold: 'AI-Augmented PM Workflows:',
        text: ' Integrated Claude, ChatGPT, and Cursor into daily product operations — synthesising user research, accelerating PRD drafting, SQL-based data exploration, and edge-case test generation across 5+ concurrent projects.',
      },
    ],
  },
  {
    title: 'Technical PM — Payments, Revenue & Internal Tooling',
    period: 'Jul 2022 – Dec 2023',
    metrics: ['20% Fewer Txn Failures', '$400K+ GMV Protected', '600+ Hrs Saved/Yr', '95%+ On-Time Delivery'],
    achievements: [
      {
        bold: 'Payments & Revenue:',
        text: ' Reduced transaction failures by 20%, protecting $400K+ GMV — via API-driven checkout with real-time validation and Razorpay multi-method integration; aligned roadmap with finance leadership and engineering squads.',
      },
      {
        bold: 'Analytics Platform (0→1):',
        text: ' Built self-serve analytics system with scheduled email delivery; eliminated 60% of manual ops reporting, saving 600+ hrs/year and giving leadership real-time pipeline visibility.',
      },
      {
        bold: 'Internal B2B CRM (0→1):',
        text: ' Replaced 100% spreadsheet-based ops with a custom CRM; adopted by 20–50 team members, cutting field ops turnaround by 2–3 days.',
      },
      {
        bold: 'Engineering Efficiency:',
        text: ' Reduced rework by 30% via structured PRDs and clear acceptance criteria; delivered 95%+ roadmap on time across 5+ concurrent projects using RICE, MoSCoW, and OKR-driven planning.',
      },
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const Experience = () => {
  return (
    <Box id="experience" sx={{ py: 10, bgcolor: '#0A0A0A' }}>
      <Container maxWidth="lg">

        {/* Section Heading + Award */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography variant="h3" sx={{ mb: 2, textAlign: 'center' }}>
            Work <span style={{ color: '#4CAF50' }}>Experience</span>
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
            <AwardBadge>
              <EmojiEvents sx={{ fontSize: 17 }} />
              Best Employee — Product Team, Q1 2025 · Repos Energy
            </AwardBadge>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <StyledPaper>

                {/* Company Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap' }}>
                  <CompanyLogo src="image/Logo.svg" alt="Repos Energy Logo" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Technical Product Manager
                    </Typography>
                    <Typography variant="subtitle1" color="primary" sx={{ mt: 0.5 }}>
                      Repos Energy · Pune, India
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                      Series B EnergyTech &nbsp;·&nbsp; India's largest fuel delivery network
                      &nbsp;·&nbsp; 500K+ users &nbsp;·&nbsp; Jul 2022 – Present
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <TagChip label="Remote-Ready" size="small"
                        sx={{ bgcolor: 'rgba(76,175,80,0.10)', color: '#4CAF50', border: '1px solid rgba(76,175,80,0.22)' }} />
                      <TagChip label="Async-First" size="small"
                        sx={{ bgcolor: 'rgba(76,175,80,0.10)', color: '#4CAF50', border: '1px solid rgba(76,175,80,0.22)' }} />
                      <TagChip label="React + TypeScript" size="small"
                        sx={{ bgcolor: 'rgba(33,150,243,0.10)', color: '#2196F3', border: '1px solid rgba(33,150,243,0.22)' }} />
                      <TagChip label="PM who codes" size="small"
                        sx={{ bgcolor: 'rgba(33,150,243,0.10)', color: '#2196F3', border: '1px solid rgba(33,150,243,0.22)' }} />
                    </Box>
                  </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', my: 3 }} />

                {/* Role Blocks */}
                {roles.map((role, roleIdx) => (
                  <motion.div
                    key={roleIdx}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: roleIdx * 0.15 }}
                  >
                    <RoleBlock sx={{ mb: roleIdx < roles.length - 1 ? 3 : 0 }}>

                      {/* Role Title + Period */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
                          {role.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary"
                          sx={{ whiteSpace: 'nowrap', pt: 0.3, fontSize: '12px' }}>
                          {role.period}
                        </Typography>
                      </Box>

                      {/* Key Metric Chips */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {role.metrics.map((m, i) => (
                          <MetricChip key={i} label={m} size="small" />
                        ))}
                      </Box>

                      {/* Achievement Bullets */}
                      <Grid container spacing={1}>
                        {role.achievements.map((item, idx) => (
                          <Grid item xs={12} sm={6} key={idx}>
                            <motion.div
                              initial={{ opacity: 0, x: -14 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.4, delay: idx * 0.07 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', pr: 1 }}>
                                <CheckCircleOutline
                                  sx={{ color: '#4CAF50', mr: 1, mt: 0.35, fontSize: 16, flexShrink: 0 }}
                                />
                                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.65 }}>
                                  <strong style={{ color: '#e0e0e0' }}>{item.bold}</strong>
                                  {item.text}
                                </Typography>
                              </Box>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>

                    </RoleBlock>
                  </motion.div>
                ))}

              </StyledPaper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Experience;